import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getCurrentUser } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { format, subDays } from 'date-fns';

// ============== TYPES ==============

export interface MachineOEE {
  machine_id: string;
  machine_name: string;
  machine_code: string;
  machine_type: string;
  is_active: boolean;
  availability: number;
  performance: number;
  quality: number;
  oee: number;
  planned_time_minutes: number;
  operating_time_minutes: number;
  stop_time_minutes: number;
  total_produced: number;
  good_produced: number;
  defects: number;
  active_stops: number;
}

export interface MachineStop {
  id: string;
  machine_id: string;
  stop_type: 'planned' | 'unplanned' | 'breakdown' | 'setup' | 'maintenance' | 'material_shortage' | 'quality_issue' | 'other';
  reason: string;
  description: string | null;
  started_at: string;
  ended_at: string | null;
  duration_minutes: number | null;
  work_order_id: string | null;
  reported_by: string | null;
  resolved_by: string | null;
  resolution_notes: string | null;
  is_resolved: boolean;
  created_at: string;
  // From joins
  machine_name?: string;
  machine_code?: string;
  reported_by_name?: string;
  current_duration_minutes?: number;
}

export interface MachineProduction {
  id: string;
  machine_id: string;
  production_date: string;
  shift_id: string | null;
  work_order_id: string | null;
  planned_quantity: number;
  produced_quantity: number;
  good_quantity: number;
  defect_quantity: number;
  planned_time_minutes: number;
  actual_time_minutes: number;
  stop_time_minutes: number;
  operator_id: string | null;
  notes: string | null;
}

export interface StopFormData {
  machine_id: string;
  stop_type: string;
  reason: string;
  description?: string;
  work_order_id?: string;
}

export interface ProductionFormData {
  machine_id: string;
  production_date: string;
  shift_id?: string;
  work_order_id?: string;
  planned_quantity: number;
  produced_quantity: number;
  good_quantity: number;
  planned_time_minutes: number;
  actual_time_minutes: number;
  operator_id?: string;
  notes?: string;
}

// ============== OEE QUERIES ==============

export function useMachineOEEList() {
  return useQuery({
    queryKey: ['machine-oee-list'],
    queryFn: async () => {
      const { data, error } = await api
        .from('machine_oee_current')
        .select('*')
        .order('oee', { ascending: false });

      if (error) throw error;
      return data as MachineOEE[];
    },
    staleTime: 60000, // 1 minute
  });
}

export function useMachineOEE(machineId: string | undefined, dateFrom?: string, dateTo?: string) {
  const from = dateFrom || format(subDays(new Date(), 30), 'yyyy-MM-dd');
  const to = dateTo || format(new Date(), 'yyyy-MM-dd');

  return useQuery({
    queryKey: ['machine-oee', machineId, from, to],
    queryFn: async () => {
      if (!machineId) return null;

      const { data, error } = await api.rpc('calculate_machine_oee', {
        p_machine_id: machineId,
        p_date_from: from,
        p_date_to: to,
      });

      if (error) throw error;
      return data?.[0] || null;
    },
    enabled: !!machineId,
  });
}

export function useOEEOverview() {
  return useQuery({
    queryKey: ['oee-overview'],
    queryFn: async () => {
      const { data, error } = await api
        .from('machine_oee_current')
        .select('*')
        .eq('is_active', true);

      if (error) throw error;

      const machines = data || [];
      const total = machines.length;

      if (total === 0) {
        return {
          avgOEE: 0,
          avgAvailability: 0,
          avgPerformance: 0,
          avgQuality: 0,
          totalMachines: 0,
          machinesWithStops: 0,
          totalStops: 0,
        };
      }

      const avgOEE = machines.reduce((sum, m) => sum + (m.oee || 0), 0) / total;
      const avgAvailability = machines.reduce((sum, m) => sum + (m.availability || 0), 0) / total;
      const avgPerformance = machines.reduce((sum, m) => sum + (m.performance || 0), 0) / total;
      const avgQuality = machines.reduce((sum, m) => sum + (m.quality || 0), 0) / total;
      const machinesWithStops = machines.filter(m => m.active_stops > 0).length;
      const totalStops = machines.reduce((sum, m) => sum + (m.active_stops || 0), 0);

      return {
        avgOEE,
        avgAvailability,
        avgPerformance,
        avgQuality,
        totalMachines: total,
        machinesWithStops,
        totalStops,
      };
    },
    staleTime: 60000,
  });
}

// ============== STOP QUERIES ==============

export function useMachineStops(filters?: {
  machineId?: string;
  stopType?: string;
  isResolved?: boolean;
  dateFrom?: string;
  dateTo?: string;
}) {
  return useQuery({
    queryKey: ['machine-stops', filters],
    queryFn: async () => {
      let query = api
        .from('machine_stops')
        .select(`
          *,
          machine:machines(id, name, code),
          reported_by_profile:profiles!machine_stops_reported_by_fkey(id, full_name),
          resolved_by_profile:profiles!machine_stops_resolved_by_fkey(id, full_name),
          work_order:work_orders(id, order_number)
        `)
        .order('started_at', { ascending: false });

      if (filters?.machineId) {
        query = query.eq('machine_id', filters.machineId);
      }
      if (filters?.stopType && filters.stopType !== 'all') {
        query = query.eq('stop_type', filters.stopType);
      }
      if (filters?.isResolved !== undefined) {
        query = query.eq('is_resolved', filters.isResolved);
      }
      if (filters?.dateFrom) {
        query = query.gte('started_at', filters.dateFrom);
      }
      if (filters?.dateTo) {
        query = query.lte('started_at', filters.dateTo + 'T23:59:59');
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data || []).map((s: any) => ({
        ...s,
        machine_name: s.machine?.name,
        machine_code: s.machine?.code,
        reported_by_name: s.reported_by_profile?.full_name,
        resolved_by_name: s.resolved_by_profile?.full_name,
        work_order_number: s.work_order?.order_number,
      })) as MachineStop[];
    },
  });
}

export function useActiveStops() {
  return useQuery({
    queryKey: ['active-stops'],
    queryFn: async () => {
      const { data, error } = await api
        .from('machine_stops_active')
        .select('*');

      if (error) throw error;
      return data as MachineStop[];
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

// ============== PRODUCTION QUERIES ==============

export function useMachineProduction(filters?: {
  machineId?: string;
  dateFrom?: string;
  dateTo?: string;
  shiftId?: string;
}) {
  return useQuery({
    queryKey: ['machine-production', filters],
    queryFn: async () => {
      let query = api
        .from('machine_production')
        .select(`
          *,
          machine:machines(id, name, code),
          shift:shifts(id, name),
          operator:profiles!machine_production_operator_id_fkey(id, full_name)
        `)
        .order('production_date', { ascending: false });

      if (filters?.machineId) {
        query = query.eq('machine_id', filters.machineId);
      }
      if (filters?.dateFrom) {
        query = query.gte('production_date', filters.dateFrom);
      }
      if (filters?.dateTo) {
        query = query.lte('production_date', filters.dateTo);
      }
      if (filters?.shiftId) {
        query = query.eq('shift_id', filters.shiftId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as MachineProduction[];
    },
  });
}

// ============== MUTATIONS ==============

export function useRegisterStop() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: StopFormData) => {
      const user = getCurrentUser();

      const { data: result, error } = await api.rpc('register_machine_stop', {
        p_machine_id: data.machine_id,
        p_stop_type: data.stop_type,
        p_reason: data.reason,
        p_description: data.description || null,
        p_work_order_id: data.work_order_id || null,
        p_reported_by: user?.id,
      });

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machine-stops'] });
      queryClient.invalidateQueries({ queryKey: ['active-stops'] });
      queryClient.invalidateQueries({ queryKey: ['machine-oee-list'] });
      queryClient.invalidateQueries({ queryKey: ['oee-overview'] });
      toast({ title: 'Parada registrada' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error al registrar parada',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useResolveStop() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      stopId,
      resolutionNotes,
    }: {
      stopId: string;
      resolutionNotes?: string;
    }) => {
      const user = getCurrentUser();

      const { error } = await api.rpc('resolve_machine_stop', {
        p_stop_id: stopId,
        p_resolved_by: user?.id,
        p_resolution_notes: resolutionNotes || null,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machine-stops'] });
      queryClient.invalidateQueries({ queryKey: ['active-stops'] });
      queryClient.invalidateQueries({ queryKey: ['machine-oee-list'] });
      queryClient.invalidateQueries({ queryKey: ['oee-overview'] });
      toast({ title: 'Parada resuelta' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error al resolver parada',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useRecordProduction() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: ProductionFormData) => {
      const user = getCurrentUser();

      const { data: result, error } = await api
        .from('machine_production')
        .upsert([{
          ...data,
          operator_id: data.operator_id || user?.id,
        }], { onConflict: 'machine_id,production_date,shift_id' })
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machine-production'] });
      queryClient.invalidateQueries({ queryKey: ['machine-oee-list'] });
      queryClient.invalidateQueries({ queryKey: ['machine-oee'] });
      queryClient.invalidateQueries({ queryKey: ['oee-overview'] });
      toast({ title: 'Producción registrada' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error al registrar producción',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// ============== HELPER FUNCTIONS ==============

export function getStopTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    planned: 'Planificada',
    unplanned: 'No planificada',
    breakdown: 'Avería',
    setup: 'Configuración',
    maintenance: 'Mantenimiento',
    material_shortage: 'Falta de material',
    quality_issue: 'Problema de calidad',
    other: 'Otra',
  };
  return labels[type] || type;
}

export function getStopTypeColor(type: string): string {
  const colors: Record<string, string> = {
    planned: 'bg-blue-100 text-blue-800',
    unplanned: 'bg-yellow-100 text-yellow-800',
    breakdown: 'bg-red-100 text-red-800',
    setup: 'bg-purple-100 text-purple-800',
    maintenance: 'bg-green-100 text-green-800',
    material_shortage: 'bg-orange-100 text-orange-800',
    quality_issue: 'bg-pink-100 text-pink-800',
    other: 'bg-gray-100 text-gray-800',
  };
  return colors[type] || 'bg-gray-100 text-gray-800';
}

export function getOEEColor(oee: number): string {
  if (oee >= 85) return 'text-green-600';
  if (oee >= 65) return 'text-yellow-600';
  return 'text-red-600';
}

export function getOEEStatus(oee: number): string {
  if (oee >= 85) return 'Excelente';
  if (oee >= 65) return 'Aceptable';
  if (oee >= 40) return 'Bajo';
  return 'Crítico';
}
